// Brand frame and plain text → HTML render of the platform's e-mails (Tallerza
// brand, not a workshop's): HTML with inline styles and table layout (what
// mail clients render). Shared so the backoffice preview of an editable
// e-mail (correosTipo) is byte for byte what the Function sends. Pure
// functions: everything that comes from a person is escaped here, so callers
// pass raw values. No external resources except the logo served by tallerza.cl.

export type Plantilla = { asunto: string; html: string; texto: string };

// Brand tokens (docs/landing/design_handoff_tallerza_landing/tokens/tokens.css).
export const COLORES_CORREO = {
  fondo: '#FBF9F5',
  superficie: '#FFFFFF',
  muted: '#F2EDE5',
  borde: '#E8E3DB',
  tinta: '#1A1718',
  texto2: '#625C5A',
  primario: '#E11D74',
  primarioTexto: '#C4105F',
} as const;
const C = COLORES_CORREO;
export const FUENTE_TITULO_CORREO = "Sora, 'Segoe UI', Helvetica, Arial, sans-serif";
export const FUENTE_CORREO = "'Atkinson Hyperlegible', 'Segoe UI', Helvetica, Arial, sans-serif";
const FUENTE_TITULO = FUENTE_TITULO_CORREO;
const FUENTE = FUENTE_CORREO;

export const LOGO_URL = 'https://tallerza.cl/apple-touch-icon.png';
export const ADMIN_URL = 'https://admin.tallerza.cl';
export const APP_URL = 'https://app.tallerza.cl';
/** The inspection app's page in Google Play. */
export const PLAY_URL = 'https://play.google.com/store/apps/details?id=cl.tallerza.inspeccion';
/** iOS is not published yet: the e-mails say so in this one line. */
export const LINEA_IOS =
  '¿iPhone o iPad? La app llega pronto a App Store; mientras, usa app.tallerza.cl en el navegador.';

const ENTIDADES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/** Text or attribute value safe to place in HTML (quotes included). */
export function escaparHtml(v: unknown): string {
  return String(v ?? '').replace(/[&<>"']/g, (c) => ENTIDADES[c] ?? c);
}

// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]+/g;

/** One-line text (subjects, plain-text values): no control characters, bounded. */
export function unaLinea(v: unknown, max = 200): string {
  return String(v ?? '')
    .replace(CONTROL, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
}

/** Call-to-action button. `href` and `texto` are raw (escaped here). */
export function botonCorreo(href: string, texto: string): string {
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:28px 0"><tr><td style="border-radius:999px;background:${C.primario}"><a href="${escaparHtml(href)}" style="display:inline-block;padding:15px 30px;border-radius:999px;background:${C.primario};color:#FFFFFF;font-family:${FUENTE_TITULO};font-size:18px;font-weight:700;line-height:1.2;text-decoration:none">${escaparHtml(texto)}</a></td></tr></table>`;
}

/** Paragraph; `html` is trusted. */
export const parrafoCorreo = (html: string) =>
  `<p style="margin:0 0 16px;font-family:${FUENTE};font-size:16px;line-height:1.55;color:${C.tinta}">${html}</p>`;

/** Small grey note; `html` is trusted. */
export const notaCorreo = (html: string) =>
  `<p style="margin:0 0 12px;font-family:${FUENTE};font-size:14px;line-height:1.5;color:${C.texto2}">${html}</p>`;

/** Page frame: logo + wordmark, white card, footer. `cuerpo` and `pie` are trusted HTML. */
export function marcoCorreo({
  titulo,
  resumen,
  cuerpo,
  pie,
}: {
  titulo: string;
  resumen: string;
  cuerpo: string;
  pie: string;
}): string {
  return `<!doctype html>
<html lang="es-CL">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="color-scheme" content="light">
<meta name="supported-color-schemes" content="light">
<title>${escaparHtml(titulo)}</title>
</head>
<body style="margin:0;padding:0;background:${C.fondo}">
<div style="display:none;max-height:0;overflow:hidden;opacity:0">${escaparHtml(resumen)}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${C.fondo}">
<tr><td align="center" style="padding:32px 16px">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:560px">
<tr><td style="padding:0 4px 20px">
<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
<td style="vertical-align:middle"><img src="${LOGO_URL}" width="32" height="32" alt="Tallerza" style="display:block;border:0;border-radius:8px"></td>
<td style="vertical-align:middle;padding-left:10px;font-family:${FUENTE_TITULO};font-size:24px;font-weight:700;letter-spacing:-0.04em;color:${C.tinta}">tallerza</td>
</tr></table>
</td></tr>
<tr><td style="background:${C.superficie};border-radius:24px;padding:32px 28px">
<h1 style="margin:0 0 16px;font-family:${FUENTE_TITULO};font-size:24px;font-weight:700;letter-spacing:-0.03em;line-height:1.25;color:${C.tinta}">${escaparHtml(titulo)}</h1>
${cuerpo}
</td></tr>
<tr><td style="padding:20px 4px 0;font-family:${FUENTE};font-size:13px;line-height:1.5;color:${C.texto2}">${pie}</td></tr>
</table>
</td></tr>
</table>
</body>
</html>`;
}

export const PIE_PLATAFORMA = `Tallerza es un producto de Kalfuntech · <a href="https://tallerza.cl" style="color:${C.primarioTexto}">tallerza.cl</a>`;

/**
 * Bare https links inside already-escaped text. `&` only as `&amp;` (what
 * escaparHtml leaves), so no other entity (`&quot;`, `&#39;`) is swallowed and
 * the match is always safe inside an href. Ends on a letter, digit, / or =
 * (a period or colon after a link is the sentence's).
 */
const ENLACE = /https:\/\/(?:[A-Za-z0-9.\-/_?=#%~+:]|&amp;)*[A-Za-z0-9/=]/g;

/** Escaped inline text: **bold** and bare https links. */
function enLinea(texto: string): string {
  return escaparHtml(texto)
    .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
    .replace(ENLACE, (u) => `<a href="${u}" style="color:${C.primarioTexto}">${u}</a>`);
}

const lista = (items: string[], tag: 'ul' | 'ol') =>
  `<${tag} style="margin:0 0 16px;padding-left:22px;font-family:${FUENTE};font-size:16px;line-height:1.55;color:${C.tinta}">${items
    .map((i) => `<li style="margin:0 0 6px">${enLinea(i)}</li>`)
    .join('')}</${tag}>`;

/** A paragraph that is only `[Texto](https://…)` becomes a button. */
const BOTON = /^\[([^[\]]{1,80})\]\((https:\/\/[^\s()<>"'[\]{}]+)\)$/;

/** Images are only served by the platform's own site. */
export const PREFIJO_IMAGEN_CORREO = 'https://tallerza.cl/';
/** A line written as an image, `![alt](url)`, valid or not. */
const MARCA_IMAGEN = /^!\[([^[\]]*)\]\(([^()]*)\)$/;
/** Path after the prefix: plain characters only (no quotes, spaces, `?`, `#`, `%`). */
const RUTA_IMAGEN = /^[A-Za-z0-9._~\-/]+$/;
export const MAX_ALT_IMAGEN = 200;

export type ImagenCorreo = { alt: string; src: string };

/**
 * Reads a line written as `![alt](url)`: the image when it is valid, an error
 * (in Spanish, for the editor) when it is not, or null when the line is not an
 * image at all. Valid = non-empty alt and a URL under exactly
 * https://tallerza.cl/ with a plain path (no `..`, empty segment, query or
 * fragment).
 */
export function leerImagenCorreo(linea: string): ImagenCorreo | { error: string } | null {
  const m = MARCA_IMAGEN.exec(linea.trim());
  if (!m) return null;
  const alt = unaLinea(m[1], 1000);
  const src = (m[2] ?? '').trim();
  const ruta = src.startsWith(PREFIJO_IMAGEN_CORREO) ? src.slice(PREFIJO_IMAGEN_CORREO.length) : '';
  if (!RUTA_IMAGEN.test(ruta) || ruta.split('/').some((t) => t === '..' || t === '.' || t === ''))
    return {
      error: `Las imágenes solo pueden venir de ${PREFIJO_IMAGEN_CORREO} (por ejemplo, ${PREFIJO_IMAGEN_CORREO}correos/gemelo.webp), sin espacios, comillas, «?» ni «#».`,
    };
  if (!alt)
    return {
      error:
        'Cada imagen necesita un texto que la describa: ![Texto de la imagen](https://tallerza.cl/…).',
    };
  if (alt.length > MAX_ALT_IMAGEN)
    return { error: `El texto de una imagen puede tener hasta ${MAX_ALT_IMAGEN} caracteres.` };
  return { alt, src };
}

const esImagen = (x: ReturnType<typeof leerImagenCorreo>): x is ImagenCorreo =>
  x !== null && !('error' in x);

/** Full-width image (504 = card width, for clients that ignore CSS). Escaped here. */
export function imagenCorreo({ alt, src }: ImagenCorreo): string {
  return `<img src="${escaparHtml(src)}" alt="${escaparHtml(alt)}" width="504" style="display:block;width:100%;max-width:560px;height:auto;margin:8px 0 20px;border:0;border-radius:16px;outline:none;text-decoration:none">`;
}

/**
 * Free text written in the backoffice as HTML: blank lines separate
 * paragraphs, "- " lines are a bullet list, "1. " lines a numbered one,
 * **bold**, bare https links, a paragraph that is only
 * `[Texto](https://…)` is a button and one that is only
 * `![Texto](https://tallerza.cl/…)` an image (any other URL stays text). Everything is escaped: the text is the
 * superadmin's, but a lead's name and workshop came from a public form.
 */
export function textoAHtml(texto: string): string {
  const bloques = texto
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((b) => b.split('\n').filter((l) => l.trim() !== ''))
    .filter((b) => b.length);
  return bloques
    .map((lineas) => {
      const boton = lineas.length === 1 ? BOTON.exec((lineas[0] ?? '').trim()) : null;
      if (boton) return botonCorreo(boton[2] ?? '', boton[1] ?? '');
      const imagen = lineas.length === 1 ? leerImagenCorreo(lineas[0] ?? '') : null;
      if (esImagen(imagen)) return imagenCorreo(imagen);
      if (lineas.every((l) => /^- /.test(l)))
        return lista(
          lineas.map((l) => l.slice(2)),
          'ul',
        );
      if (lineas.every((l) => /^\d+\. /.test(l)))
        return lista(
          lineas.map((l) => l.replace(/^\d+\. /, '')),
          'ol',
        );
      // A line written as an image that is not one (other site, no alt, or
      // inside a longer paragraph) is shown as written, never linked.
      return parrafoCorreo(
        lineas.map((l) => (leerImagenCorreo(l) ? escaparHtml(l) : enLinea(l))).join('<br>'),
      );
    })
    .join('\n');
}

/**
 * Plain-text part: a button paragraph reads "Texto: https://…" and an image
 * "[Imagen: alt]" (the picture's URL says nothing to a reader).
 */
export function textoPlano(texto: string): string {
  return texto
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((l) => {
      const b = BOTON.exec(l.trim());
      if (b) return `${b[1]}: ${b[2]}`;
      const i = leerImagenCorreo(l);
      return esImagen(i) ? `[Imagen: ${i.alt}]` : l;
    })
    .join('\n');
}

/**
 * An e-mail whose body is free text (Correo A, correos tipo): the subject is
 * the title, `resumen` the inbox preview and `pie` why the person gets it
 * (plain text, escaped here; the platform line is added after it).
 */
export function correoLibre({
  asunto,
  texto,
  resumen,
  pie,
}: {
  asunto: string;
  texto: string;
  resumen: string;
  pie: string;
}): Plantilla {
  const titulo = unaLinea(asunto, 200);
  const html = marcoCorreo({
    titulo,
    resumen,
    cuerpo: textoAHtml(texto),
    pie: `${escaparHtml(pie)} ${PIE_PLATAFORMA}`,
  });
  return { asunto: titulo, html, texto: textoPlano(texto) };
}
